import type {
  Party,
  Deliverable,
  DeliveryItem,
  DeliveryItemDetailFields,
  ConnectedAccount,
} from "@/types/delivery-manager.types"

// ═══════════════════════════════════════════════════════════════════════
//  Mock Parties (reusable references)
// ═══════════════════════════════════════════════════════════════════════

export const PARTIES = {
  you:     { id: "p-1", name: "You",            initials: "JL" } satisfies Party,
  sony:    { id: "p-2", name: "Sony Music",      initials: "SM" } satisfies Party,
  alex:    { id: "p-3", name: "Alex Rivera",     initials: "AR" } satisfies Party,
  netflix: { id: "p-4", name: "Netflix",          initials: "NF" } satisfies Party,
  nike:    { id: "p-5", name: "Nike Creative",   initials: "NK" } satisfies Party,
  morgan:  { id: "p-6", name: "Morgan Chen",     initials: "MC" } satisfies Party,
  sunset:  { id: "p-7", name: "Sunset Sound",    initials: "SS" } satisfies Party,
} as const

// ═══════════════════════════════════════════════════════════════════════
//  Mock Deliverables (table view)
// ═══════════════════════════════════════════════════════════════════════

export const mockDeliverables: Deliverable[] = [
  { id: "del-1", title: "Execute Recording Agreement", dealName: "Sony Music Partnership", dueDate: "2026-03-15", status: "in_progress", assignee: "Jordan Lee", priority: "high" },
  { id: "del-2", title: "Deliver Master Recordings", dealName: "Sony Music Partnership", dueDate: "2026-04-01", status: "pending", assignee: "Alex Rivera", priority: "high" },
  { id: "del-3", title: "Submit Sync License Approval", dealName: "Netflix Sync License", dueDate: "2026-03-10", status: "overdue", assignee: "Morgan Chen", priority: "high" },
  { id: "del-4", title: "Finalize Brand Usage Terms", dealName: "Nike Brand Ambassador", dueDate: "2026-03-20", status: "pending", assignee: "Jordan Lee", priority: "medium" },
  { id: "del-5", title: "Complete Publishing Registration", dealName: "Sony Music Partnership", dueDate: "2026-02-28", status: "completed", assignee: "Alex Rivera", priority: "low" },
  { id: "del-6", title: "Review Merchandising Samples", dealName: "Nike Brand Ambassador", dueDate: "2026-03-25", status: "pending", assignee: "Morgan Chen", priority: "medium" },
]

// ═══════════════════════════════════════════════════════════════════════
//  Mock Delivery Items (feed view)
// ═══════════════════════════════════════════════════════════════════════

export const mockDeliveryItems: DeliveryItem[] = [
  // ── SEND + PAST ──
  { id: "f-12", kind: "payment", title: "Engineer Deposit", description: "Mixing engineer retainer for EP sessions", dueDate: "2026-02-10", status: "completed", dealId: "deal-1", dealName: "Sony Music Partnership", from: PARTIES.you, to: PARTIES.alex, direction: "send", amountCents: 500_000, currency: "USD" },
  { id: "f-13", kind: "artifact", title: "Signed Recording Agreement", description: "Fully executed recording contract PDF", dueDate: "2026-02-14", status: "completed", dealId: "deal-1", dealName: "Sony Music Partnership", from: PARTIES.you, to: PARTIES.sony, direction: "send", category: "document", fileType: "Document" },
  { id: "f-14", kind: "artifact", title: "Brand Likeness Approval", description: "Signed likeness release for Nike campaign", dueDate: "2026-02-20", status: "completed", dealId: "deal-5", dealName: "Nike Brand Ambassador", from: PARTIES.you, to: PARTIES.nike, direction: "send", category: "document", fileType: "Document" },

  // ── SEND + UPCOMING ──
  { id: "f-5", kind: "payment", title: "Producer Fee", description: "Production fee for tracks 1-4 — triggers when masters are delivered", dueDate: "2026-03-15", status: "upcoming", dealId: "deal-1", dealName: "Sony Music Partnership", from: PARTIES.you, to: PARTIES.alex, direction: "send", amountCents: 1_200_000, currency: "USD", automation: { type: "on_delivery", deliverableId: "f-2", deliverableTitle: "Upload Sample Masters" } },
  { id: "f-2", kind: "artifact", title: "Upload Sample Masters", description: "Rough mixes for tracks 1-4 of the EP", dueDate: "2026-03-05", status: "in_progress", dealId: "deal-1", dealName: "Sony Music Partnership", from: PARTIES.you, to: PARTIES.sony, direction: "send", category: "audio", fileType: "Audio Files" },
  { id: "f-8", kind: "artifact", title: "Upload Sample Reel", description: "30-second campaign video cut for approval", dueDate: "2026-03-08", status: "pending", dealId: "deal-5", dealName: "Nike Brand Ambassador", from: PARTIES.you, to: PARTIES.nike, direction: "send", category: "video", fileType: "Video File" },
  { id: "f-10", kind: "artifact", title: "Recording Session Stems", description: "Multi-track stems for remix clearance", dueDate: "2026-03-14", status: "in_progress", dealId: "deal-2", dealName: "Netflix Sync License", from: PARTIES.morgan, to: PARTIES.netflix, direction: "send", category: "audio", fileType: "Audio Files" },
  { id: "f-11", kind: "payment", title: "Studio Rental", description: "March session block at Sunset Sound", dueDate: "2026-03-22", status: "upcoming", dealId: "deal-1", dealName: "Sony Music Partnership", from: PARTIES.you, to: PARTIES.sunset, direction: "send", amountCents: 850_000, currency: "USD", automation: { type: "on_date", date: "2026-03-22" } },
  { id: "f-16", kind: "payment", title: "Final Mix Bonus", description: "Completion bonus pending A&R sign-off", dueDate: "2026-03-28", status: "pending", dealId: "deal-1", dealName: "Sony Music Partnership", from: PARTIES.you, to: PARTIES.alex, direction: "send", amountCents: 350_000, currency: "USD", automation: { type: "requires_approval", approverId: "p-2", approverName: "Sony Music" } },

  // ── RECEIVE + PAST ──
  { id: "f-1", kind: "payment", title: "Advance Payment", description: "Recording advance per Section 4.2", dueDate: "2026-02-15", status: "completed", dealId: "deal-1", dealName: "Sony Music Partnership", from: PARTIES.sony, to: PARTIES.you, direction: "receive", amountCents: 7_500_000, currency: "USD" },
  { id: "f-6", kind: "artifact", title: "Receipt of Recording Session", description: "Studio invoice and session logs from Feb sessions", dueDate: "2026-02-28", status: "completed", dealId: "deal-1", dealName: "Sony Music Partnership", from: PARTIES.alex, to: PARTIES.you, direction: "receive", category: "document", fileType: "Document" },
  { id: "f-15", kind: "payment", title: "Catalog Royalties", description: "Q4 2025 streaming royalties", dueDate: "2026-01-30", status: "completed", dealId: "deal-1", dealName: "Sony Music Partnership", from: PARTIES.sony, to: PARTIES.you, direction: "receive", amountCents: 1_120_000, currency: "USD", automation: { type: "streaming", source: "Spotify / Apple Music", unitLabel: "per stream" } },

  // ── RECEIVE + UPCOMING ──
  { id: "f-3", kind: "payment", title: "Sync License Fee", description: "One-time sync placement fee for Season 3 — triggers on delivery of stems", dueDate: "2026-03-10", status: "overdue", dealId: "deal-2", dealName: "Netflix Sync License", from: PARTIES.netflix, to: PARTIES.you, direction: "receive", amountCents: 2_500_000, currency: "USD", automation: { type: "on_delivery", deliverableId: "f-10", deliverableTitle: "Recording Session Stems" } },
  { id: "f-4", kind: "artifact", title: "Photoshoot Pictures", description: "Campaign imagery for Spring 2026 collection", dueDate: "2026-03-12", status: "pending", dealId: "deal-5", dealName: "Nike Brand Ambassador", from: PARTIES.nike, to: PARTIES.you, direction: "receive", category: "image", fileType: "Image Files" },
  { id: "f-7", kind: "payment", title: "Brand Ambassador Fee", description: "Q1 2026 ambassador retainer", dueDate: "2026-03-20", status: "upcoming", dealId: "deal-5", dealName: "Nike Brand Ambassador", from: PARTIES.nike, to: PARTIES.you, direction: "receive", amountCents: 5_000_000, currency: "USD", automation: { type: "recurring", frequency: "quarterly", nextDate: "2026-03-20" } },
  { id: "f-9", kind: "payment", title: "Mechanical Royalty Advance", description: "Q1 mechanical royalty advance", dueDate: "2026-04-01", status: "upcoming", dealId: "deal-1", dealName: "Sony Music Partnership", from: PARTIES.sony, to: PARTIES.you, direction: "receive", amountCents: 1_850_000, currency: "USD", automation: { type: "recurring", frequency: "quarterly", nextDate: "2026-04-01" } },
]

// ═══════════════════════════════════════════════════════════════════════
//  Mock Detail Data (enriched fields for sheet view)
// ═══════════════════════════════════════════════════════════════════════

export const emptyDetail: DeliveryItemDetailFields = {
  activityLog: [],
  comments: [],
  relatedItems: [],
}

export const mockDetailData: Record<string, DeliveryItemDetailFields> = {
  // f-1: Advance Payment — completed wire with transaction
  "f-1": {
    activityLog: [
      { id: "al-1", timestamp: "2026-02-10T09:00:00Z", type: "created", actor: PARTIES.sony, description: "Payment created for recording advance" },
      { id: "al-2", timestamp: "2026-02-13T14:30:00Z", type: "payment_submitted", actor: PARTIES.sony, description: "Wire transfer initiated" },
      { id: "al-3", timestamp: "2026-02-15T10:15:00Z", type: "payment_processed", actor: PARTIES.sony, description: "Wire transfer completed — funds received" },
      { id: "al-4", timestamp: "2026-02-15T10:16:00Z", type: "status_changed", actor: PARTIES.sony, description: "Status changed to Completed" },
    ],
    comments: [
      { id: "c-1", author: PARTIES.sony, createdAt: "2026-02-13T14:35:00Z", text: "Wire initiated — should arrive within 1-2 business days." },
      { id: "c-2", author: PARTIES.you, createdAt: "2026-02-15T10:20:00Z", text: "Received, thank you!" },
    ],
    relatedItems: [
      { id: "f-2", title: "Upload Sample Masters", kind: "artifact", relationship: "same_deal" },
      { id: "f-5", title: "Producer Fee", kind: "payment", relationship: "same_deal" },
    ],
    method: { type: "wire", bankName: "JPMorgan Chase", accountLast4: "4821" },
    transaction: {
      referenceId: "WR-2026-0215-7839",
      processedAt: "2026-02-15T10:15:00Z",
      memo: "Recording advance per Section 4.2 of agreement",
      feesCents: 2_500,
      netAmountCents: 7_497_500,
    },
  },

  // f-15: Catalog Royalties — streaming with breakdown
  "f-15": {
    activityLog: [
      { id: "al-5", timestamp: "2026-01-15T08:00:00Z", type: "automation_triggered", actor: PARTIES.sony, description: "Q4 2025 streaming royalty calculation triggered" },
      { id: "al-6", timestamp: "2026-01-28T11:00:00Z", type: "payment_submitted", actor: PARTIES.sony, description: "Royalty payment submitted via ACH" },
      { id: "al-7", timestamp: "2026-01-30T09:00:00Z", type: "payment_processed", actor: PARTIES.sony, description: "Royalty payment processed" },
    ],
    comments: [
      { id: "c-3", author: PARTIES.sony, createdAt: "2026-01-28T11:05:00Z", text: "Q4 royalty statement attached. Streams up 18% QoQ." },
    ],
    relatedItems: [
      { id: "f-9", title: "Mechanical Royalty Advance", kind: "payment", relationship: "same_deal" },
    ],
    method: { type: "ach", bankName: "JPMorgan Chase", accountLast4: "4821" },
    transaction: {
      referenceId: "ACH-2026-0130-2211",
      processedAt: "2026-01-30T09:00:00Z",
      memo: "Q4 2025 streaming royalties — catalog",
      feesCents: 0,
      netAmountCents: 1_120_000,
    },
    streamingBreakdown: [
      { platform: "Spotify", plays: 2_840_000, amountCents: 620_000, period: "Q4 2025" },
      { platform: "Apple Music", plays: 1_120_000, amountCents: 310_000, period: "Q4 2025" },
      { platform: "YouTube Music", plays: 680_000, amountCents: 95_000, period: "Q4 2025" },
      { platform: "Amazon Music", plays: 420_000, amountCents: 55_000, period: "Q4 2025" },
      { platform: "Tidal", plays: 95_000, amountCents: 40_000, period: "Q4 2025" },
    ],
  },

  // f-7: Brand Ambassador Fee — recurring with history
  "f-7": {
    activityLog: [
      { id: "al-8", timestamp: "2025-12-20T10:00:00Z", type: "created", actor: PARTIES.nike, description: "Quarterly ambassador retainer created" },
      { id: "al-9", timestamp: "2026-01-02T09:00:00Z", type: "reminder_sent", actor: PARTIES.nike, description: "Upcoming payment reminder sent" },
    ],
    comments: [
      { id: "c-4", author: PARTIES.nike, createdAt: "2025-12-20T10:05:00Z", text: "Recurring quarterly retainer set up per ambassador agreement Section 3." },
      { id: "c-5", author: PARTIES.you, createdAt: "2025-12-20T12:00:00Z", text: "Confirmed — schedule looks good." },
    ],
    relatedItems: [
      { id: "f-14", title: "Brand Likeness Approval", kind: "artifact", relationship: "same_deal" },
      { id: "f-4", title: "Photoshoot Pictures", kind: "artifact", relationship: "same_deal" },
    ],
    method: { type: "ach", bankName: "Bank of America", accountLast4: "9012" },
    recurringHistory: [
      { id: "rh-1", date: "2025-06-20", amountCents: 5_000_000, status: "completed" },
      { id: "rh-2", date: "2025-09-20", amountCents: 5_000_000, status: "completed" },
      { id: "rh-3", date: "2025-12-20", amountCents: 5_000_000, status: "completed" },
      { id: "rh-4", date: "2026-03-20", amountCents: 5_000_000, status: "pending" },
    ],
  },

  // f-5: Producer Fee — on_delivery automation linking to artifact f-2
  "f-5": {
    activityLog: [
      { id: "al-10", timestamp: "2026-01-15T14:00:00Z", type: "created", actor: PARTIES.you, description: "Producer fee created — triggers on masters delivery" },
      { id: "al-11", timestamp: "2026-02-20T09:00:00Z", type: "reminder_sent", actor: PARTIES.you, description: "Reminder: awaiting sample masters delivery" },
    ],
    comments: [
      { id: "c-6", author: PARTIES.alex, createdAt: "2026-01-15T15:00:00Z", text: "Fee agreed — will be released once masters are uploaded and approved." },
    ],
    relatedItems: [
      { id: "f-2", title: "Upload Sample Masters", kind: "artifact", relationship: "blocks" },
      { id: "f-1", title: "Advance Payment", kind: "payment", relationship: "same_deal" },
    ],
    method: { type: "platform_escrow" },
  },

  // f-2: Upload Sample Masters — artifact with file info, versions, approval, comments
  "f-2": {
    activityLog: [
      { id: "al-12", timestamp: "2026-02-01T10:00:00Z", type: "created", actor: PARTIES.you, description: "Artifact created — rough mixes for tracks 1-4" },
      { id: "al-13", timestamp: "2026-02-15T16:00:00Z", type: "uploaded", actor: PARTIES.you, description: "Version 1 uploaded — initial rough mixes" },
      { id: "al-14", timestamp: "2026-02-22T11:00:00Z", type: "rejected", actor: PARTIES.sony, description: "Revision requested — track 3 needs vocal level adjustment" },
      { id: "al-15", timestamp: "2026-02-28T14:00:00Z", type: "uploaded", actor: PARTIES.you, description: "Version 2 uploaded — revised track 3 mix" },
      { id: "al-16", timestamp: "2026-03-02T09:00:00Z", type: "uploaded", actor: PARTIES.you, description: "Version 3 uploaded — final adjustments to all tracks" },
    ],
    comments: [
      { id: "c-7", author: PARTIES.sony, createdAt: "2026-02-22T11:05:00Z", text: "Track 3 vocals are buried in the mix — please bring up 2-3dB and resubmit." },
      { id: "c-8", author: PARTIES.you, createdAt: "2026-02-22T14:00:00Z", text: "Got it — will have the revised mix by end of week." },
      { id: "c-9", author: PARTIES.you, createdAt: "2026-02-28T14:05:00Z", text: "V2 uploaded with vocal adjustments. Also tweaked the low end on tracks 1 and 4." },
      { id: "c-10", author: PARTIES.sony, createdAt: "2026-03-01T10:00:00Z", text: "Much better! Minor note: can we get a slight reverb tail on the outro of track 2?" },
      { id: "c-11", author: PARTIES.you, createdAt: "2026-03-02T09:05:00Z", text: "V3 uploaded — added subtle plate reverb on track 2 outro. Ready for final review." },
    ],
    relatedItems: [
      { id: "f-5", title: "Producer Fee", kind: "payment", relationship: "triggered_by" },
      { id: "f-1", title: "Advance Payment", kind: "payment", relationship: "same_deal" },
    ],
    file: {
      fileName: "sample-masters-v3.zip",
      fileSizeBytes: 284_000_000,
      mimeType: "application/zip",
      uploadedAt: "2026-03-02T09:00:00Z",
      uploadedBy: PARTIES.you,
    },
    versions: [
      { id: "v-1", versionNumber: 1, uploadedAt: "2026-02-15T16:00:00Z", uploadedBy: PARTIES.you, changeNotes: "Initial rough mixes for tracks 1-4", fileSizeBytes: 268_000_000 },
      { id: "v-2", versionNumber: 2, uploadedAt: "2026-02-28T14:00:00Z", uploadedBy: PARTIES.you, changeNotes: "Revised track 3 vocal levels (+2.5dB), adjusted low end on tracks 1 & 4", fileSizeBytes: 275_000_000 },
      { id: "v-3", versionNumber: 3, uploadedAt: "2026-03-02T09:00:00Z", uploadedBy: PARTIES.you, changeNotes: "Added plate reverb on track 2 outro, final adjustments", fileSizeBytes: 284_000_000 },
    ],
    approval: {
      status: "pending_review",
      comments: "Awaiting final review of V3",
    },
  },
}

// ═══════════════════════════════════════════════════════════════════════
//  Balance Types & Mock Data
// ═══════════════════════════════════════════════════════════════════════

export const mockConnectedAccounts: ConnectedAccount[] = [
  { id: "acct-1", label: "Business Checking", institution: "Chase", lastFour: "4821", balanceCents: 18_420_00, type: "checking" },
  { id: "acct-2", label: "Savings", institution: "Chase", lastFour: "7733", balanceCents: 42_650_00, type: "savings" },
  { id: "acct-3", label: "Visa ••4012", institution: "Stripe", lastFour: "4012", balanceCents: 1_230_00, type: "card" },
]

export const EQUALIQ_BALANCE_CENTS = 24_750_00
